import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import Dropdown from '../Dropdown'
import DropdownItem from '../DropdownItem'
import SlideUp from '../SlideUp'

import * as Styled from './styled'

interface INumericStepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  presets?: number[]
  formatLabel?: (value: number) => string
  formatHeaderValue?: (value: number) => string
  decrementTestId?: string
  incrementTestId?: string
}

const defaultFormatLabel = (v: number) => String(v)

const stopTogglePropagation = (e: React.SyntheticEvent) => {
  e.stopPropagation()
}

const NumericStepper: React.FC<INumericStepperProps> = ({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  presets,
  formatLabel = defaultFormatLabel,
  formatHeaderValue = defaultFormatLabel,
  decrementTestId = 'numeric-stepper-decrement',
  incrementTestId = 'numeric-stepper-increment'
}) => {
  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()
  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    openCallback,
    closeCallback
  )
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const [draft, setDraft] = useState(() => formatHeaderValue(value))
  const inputRef = useRef<HTMLInputElement>(null)

  const hasPresets = Boolean(presets && presets.length > 0)
  const frameOpen = isOpen && hasPresets
  const useSlideUpPresets = Boolean(isTablet && hasPresets)

  useEffect(() => {
    if (!isOpen) {
      setDraft(formatHeaderValue(value))
    }
  }, [isOpen, value, formatHeaderValue])

  useLayoutEffect(() => {
    if (isOpen && hasPresets && !useSlideUpPresets) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isOpen, hasPresets, useSlideUpPresets])

  const clamp = useCallback(
    (v: number) => Math.min(max, Math.max(min, v)),
    [min, max]
  )

  const handleDecrement = useCallback(() => {
    onChange(clamp(value - step))
  }, [onChange, clamp, value, step])

  const handleIncrement = useCallback(() => {
    onChange(clamp(value + step))
  }, [onChange, clamp, value, step])

  const handlePresetSelect = useCallback(
    (preset: number) => {
      onChange(clamp(preset))
      closeDropdown()
    },
    [onChange, clamp, closeDropdown]
  )

  const handleValueBlur = useCallback(() => {
    const trimmed = draft.trim()
    const parsed = parseFloat(trimmed.replace(',', '.'))
    if (!Number.isFinite(parsed)) {
      setDraft(formatHeaderValue(value))
      return
    }
    const next = clamp(parsed)
    onChange(next)
    setDraft(formatHeaderValue(next))
  }, [draft, value, clamp, onChange, formatHeaderValue])

  const handleValueKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur()
      }
    },
    []
  )

  const isAtMin = value <= min
  const isAtMax = value >= max

  const presetItems = useMemo(
    () =>
      presets?.map(preset => (
        <DropdownItem
          key={preset}
          title={formatLabel(preset)}
          isSelected={preset === value}
          onSelect={() => handlePresetSelect(preset)}
        />
      )),
    [presets, formatLabel, value, handlePresetSelect]
  )

  const mainContent = presetItems ? (
    <Styled.PresetList>{presetItems}</Styled.PresetList>
  ) : null

  const slideUpPresetContent = presetItems ? (
    <Styled.MobilePresetList>{presetItems}</Styled.MobilePresetList>
  ) : null

  const headerCenter = hasPresets ? (
    isOpen ? (
      <Styled.ValueInput
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleValueBlur}
        onKeyDown={handleValueKeyDown}
        onClick={stopTogglePropagation}
        onPointerDown={stopTogglePropagation}
        onMouseDown={stopTogglePropagation}
        inputMode="decimal"
        aria-label="Numeric value"
      />
    ) : (
      <Styled.ValueDisplay>{formatHeaderValue(value)}</Styled.ValueDisplay>
    )
  ) : (
    <Styled.ValueInput
      ref={inputRef}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={handleValueBlur}
      onKeyDown={handleValueKeyDown}
      onClick={stopTogglePropagation}
      onPointerDown={stopTogglePropagation}
      onMouseDown={stopTogglePropagation}
      inputMode="decimal"
      aria-label="Numeric value"
    />
  )

  return (
    <Fragment>
      <Styled.Wrapper $frameOpen={frameOpen}>
        <Styled.StepButton
          $frameOpen={frameOpen}
          onClick={handleDecrement}
          disabled={isAtMin}
          data-test-id={decrementTestId}
        >
          &ndash;
        </Styled.StepButton>

        <Styled.DropdownWrapper $isOpen={isOpen} $frameOpen={frameOpen}>
          <Dropdown
            headerContent={
              <Styled.ValueTriggerInner>
                {headerCenter}
              </Styled.ValueTriggerInner>
            }
            mainContent={useSlideUpPresets ? null : mainContent}
            isOpen={isOpen}
            toggleDropdown={toggleDropdown}
            closeDropdown={closeDropdown}
            hasTogglerArrow={false}
          />
        </Styled.DropdownWrapper>

        <Styled.StepButton
          $frameOpen={frameOpen}
          onClick={handleIncrement}
          disabled={isAtMax}
          data-test-id={incrementTestId}
        >
          +
        </Styled.StepButton>
      </Styled.Wrapper>

      {useSlideUpPresets && slideUpPresetContent ? (
        <SlideUp isOpen={isOpen} onClose={closeDropdown}>
          {slideUpPresetContent}
        </SlideUp>
      ) : null}
    </Fragment>
  )
}

export default memo(NumericStepper)
